import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import type { Environment } from "@porsche-customer/slfinrtl-environment"
import { useEffect } from "react"
import { getFeatureToggle } from "src/common/getConfig"

// static, pre-generated manifests (and matching pre-badged icons) per environment, so dealers
// can tell multiple installed instances apart at a glance (name + a small colored icon badge).
const MANIFEST_HREF_BY_ENVIRONMENT: Partial<Record<Environment, string>> = {
	dev: "/manifest-dev.json",
	local: "/manifest-local.json",
	preview: "/manifest-preview.json",
	prod: "/manifest-prod.json",
}

// injects <link rel="manifest">, gated by the "pwa-installable-enabled" feature toggle.
//
// this has to happen at runtime rather than in index.html because the same build artifact is
// deployed to dev/preview/prod and the environment is only known once the app runs in the
// browser (see @porsche-customer/slfinrtl-environment).
//
// note: the PDS partial (getMetaTagsAndIconLinks) already unconditionally renders a
// <link rel="manifest"> pointing at a Porsche-branded, icon-only manifest (no
// name/display/start_url, so it does not make the app installable on its own). when this toggle
// is enabled for a known environment we replace that link's href with our own manifest instead
// of skipping, since a manifest link is always present in the DOM.
//
// no service worker is registered: since Chrome/Edge 112, "Install app" from the browser menu no
// longer requires one (https://developer.chrome.com/blog/update-install-criteria), and the
// automatic install-prompt promotion additionally requires a `fetch` handler, which we
// deliberately don't want (see docs/pwa-installable-plan.md for the reasoning). A no-op worker
// would satisfy neither, so it's not worth the added complexity.
export const usePwaInstall = () => {
	useEffect(() => {
		if (!getFeatureToggle("pwa-installable-enabled")) {
			return
		}

		const environment = currentEnvironment()
		const manifestHref = environment ? MANIFEST_HREF_BY_ENVIRONMENT[environment] : undefined
		if (!manifestHref) {
			return
		}

		const existingLink = document.querySelector<HTMLLinkElement>(`link[rel="manifest"]`)
		if (existingLink) {
			existingLink.href = manifestHref
		} else {
			const link = document.createElement("link")
			link.rel = "manifest"
			link.href = manifestHref
			document.head.appendChild(link)
		}
	}, [])
}
